import React, { lazy, Suspense, useCallback, useState } from 'react';
import { UniformRelationshipList, UniformRelationshipListObjectType } from '@workboard/ui-relationships';
import { ThemeProvider, ScopedCssBaseline, styled, CircularProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GlobalStyle, theme } from './theme/theme';
import { FederatedAdoModal } from './krDetails/FederatedAdoModal';
import { isReadOnlyUserLicenseType } from '../utils/common';

const ActionPlanDialog = lazy(() => import('./krDetails/header/ActionPlanDialog'));

const AdoModalLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

const StyledCssBaseline = styled(ScopedCssBaseline)({
  'body #new-related-work-area &, body #edit-related-work-area &': {
    // workaround for conflicts
    '& label': {
      top: '-10px',
      left: '-13px',
    },
  },
  '*': {
    boxSizing: 'border-box !important',
  },
  '*::before': {
    boxSizing: 'border-box !important',
  },
  '*::after': {
    boxSizing: 'border-box !important',
  },
});

const Wrapper = styled('div')({
  padding: '16px 39px 0px 41px'
});

export type ObjectiveRelationshipsProps = {
  objectiveId: string;
  canEdit?: boolean;
};

const getCurrentUser = (userData: any, isReadOnly: boolean) => ({
  id: userData?.user_id,
  fullName: `${userData?.first_name} ${userData?.last_name}`,
  picture: userData.picture,
  isReadOnly,
});

function ObjectiveRelationships({ objectiveId, canEdit: canEditProp = false }: ObjectiveRelationshipsProps) {
  const { t } = useTranslation();
  const [actionPlanDialogOpen, setActionPlanDialogOpen] = useState(false);

  const workboardUrl = `${window.location.origin}/wb/vnext/okr`;
  const isSuggestionEnabled = false;
  const isReadonlyUser = isReadOnlyUserLicenseType();
  const isWsActionPlanEnabled = !!window?.wbConfig?.isWsActionPlanEnabled;
  const isCoauthorEnabled = !!window?.wbConfig?.coauthorEnabled;
  let canEdit = canEditProp;
  if (isReadonlyUser) {
    canEdit = false;
  }

  const handleActionPlanClick = useCallback(() => {
    setActionPlanDialogOpen(true);
  }, []);

  const handleCloseActionPlan = useCallback(
    ({ status, workstreamId, hasRelationship }) => {
      setActionPlanDialogOpen(false);

      if (status !== 'published') {
        return;
      }

      if (hasRelationship) {
        const channel = new BroadcastChannel('ai:actionItemUpdated');

        channel.postMessage(objectiveId);
      }

      const message = 'The action plan was successfully published';
      const url = `${window.wbConfig.baseUrl}/activity/workstreams?id=${workstreamId}`;
      const linkStyles = 'color: #0072ab; font-weight: bold; white-space: nowrap;';
      const link = workstreamId
        ? `<br /><a href="${url}" target="_blank" rel="noopener noreferrer" style="${linkStyles}">View items</a>`
        : '';

      toastr.success(`${message}${link}`, null, { timeOut: 10000 });
    },
    [objectiveId],
  );

  const currentUser = getCurrentUser(window.uData, isReadonlyUser);
  return (
    <StyledCssBaseline>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        {objectiveId && (
          <Wrapper className="objective-relationships-wrapper">
            <UniformRelationshipList
              currentUser={currentUser}
              baseUrl={workboardUrl}
              objectId={objectiveId}
              objectType={UniformRelationshipListObjectType.OBJECTIVE}
              enableSuggestions={isSuggestionEnabled}
              canEdit={canEdit}
              enableRAID
              enableSEM={false}
              onActionPlanClick={isWsActionPlanEnabled ? handleActionPlanClick : undefined}
              addRelationshipButtonText={t('Add Relationship')}
              isEnhancedADOEnabled={!!window.wbConfig.isEnhancedADOKrrUIEnabled}
              isADOKRREnabled={!!window.wbConfig.isADOKRREnabled}
              adoFormOverride={
                window.wbConfig.isEnhancedADOKrrUIEnabled
                  ? ({ objectId, relationshipId, onSave, onClose, objectType }) => (
                      <Suspense fallback={<AdoModalLoader />}>
                        <FederatedAdoModal
                          open
                          objectId={String(objectId)}
                          relationshipId={String(relationshipId)}
                          onSave={(data) => {
                            // Your federated modal should persist changes itself
                            // Then call onSave to let the list refresh+close
                            onSave(data);
                          }}
                          onClose={onClose}
                          objectType={objectType}
                        />
                      </Suspense>
                    )
                  : undefined
              }
            />
            {actionPlanDialogOpen && (
              <Suspense fallback={null}>
                <ActionPlanDialog
                  relationshipId={`${objectiveId}`}
                  userId={`${currentUser.id}`}
                  isCoAuthorActive={isCoauthorEnabled}
                  onClose={handleCloseActionPlan}
                  eventProperties={{
                    location: 'OBJECTIVE_MODAL',
                    subLocation: '',
                  }}
                />
              </Suspense>
            )}
          </Wrapper>
        )}
      </ThemeProvider>
    </StyledCssBaseline>
  );
}

export default ObjectiveRelationships;
